import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";

export default function CallbackPage() {
  const [searchParams] = useSearchParams();
  const { handleCallback, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  // Navigate once auth state is confirmed — avoids race between setUser and navigate
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/app", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Prevent double-execution in strict mode
    if (handledRef.current) return;

    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // Strip auth code from URL immediately to prevent exposure in browser history
    window.history.replaceState({}, "", window.location.pathname);

    if (errorParam) {
      setError(errorDescription || errorParam);
      return;
    }

    if (!code) {
      setError("No authorization code received");
      return;
    }

    handledRef.current = true;

    handleCallback(code).catch((err) => {
      setError(err instanceof Error ? err.message : "Authentication failed");
    });
  }, [searchParams, handleCallback]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-destructive">
            Authentication Error
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <a
            href="/login"
            className="mt-4 inline-block text-sm text-primary hover:underline"
          >
            Back to login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Completing sign-in...</p>
      </div>
    </div>
  );
}
